import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Home } from 'lucide-react'
import Modal from '../Modal/Modal'

function GroupTreeNode({ node, selectedId, onSelect, expandedIds, onToggle, depth = 0 }) {
  const hasChildren = node.children && node.children.length > 0
  const isExpanded = expandedIds.has(node.id)
  const isSelected = selectedId === node.id

  return (
    <div>
      <div
        className={[
          'flex items-center gap-1 px-2 py-1.5 rounded-lg cursor-pointer transition-colors',
          isSelected ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700',
        ].join(' ')}
        style={{ paddingLeft: `${(depth + 1) * 12}px` }}
        onClick={() => onSelect(node.id)}
      >
        <button
          type="button"
          className="w-4 h-4 flex items-center justify-center flex-shrink-0"
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.id) }}
        >
          {hasChildren ? (
            isExpanded
              ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
              : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
          ) : null}
        </button>
        <span className="text-sm truncate">{node.name}</span>
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <GroupTreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              expandedIds={expandedIds}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function MoveToGroupModal({ isOpen, onClose, onMove, itemName, currentGroupId, tree, loading }) {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState(currentGroupId ?? '__root__')
  const [expandedIds, setExpandedIds] = useState(new Set())

  const handleToggle = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const handleMove = () => {
    const groupId = selectedId === '__root__' ? null : selectedId
    onMove(groupId)
  }

  const isRootSelected = selectedId === '__root__'
  const currentKey = currentGroupId ?? '__root__'
  const unchanged = selectedId === currentKey

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('groups.moveModal.title', { name: itemName })}>
      <div className="space-y-3">
        <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto p-1">
          {/* Raiz "Principal" */}
          <div
            className={[
              'flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors',
              isRootSelected ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700',
            ].join(' ')}
            onClick={() => setSelectedId('__root__')}
          >
            <Home className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="text-sm font-medium">{t('groups.breadcrumbRoot')}</span>
            <span className="text-xs text-gray-400 ml-1">{t('groups.moveModal.noGroup')}</span>
          </div>

          {/* Árvore de grupos */}
          {tree && tree.map((node) => (
            <GroupTreeNode
              key={node.id}
              node={node}
              selectedId={selectedId}
              onSelect={setSelectedId}
              expandedIds={expandedIds}
              onToggle={handleToggle}
            />
          ))}

          {(!tree || tree.length === 0) && (
            <p className="text-xs text-gray-400 px-3 py-2">{t('groups.moveModal.emptyTree')}</p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>
            {t('common.buttons.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleMove}
            disabled={loading || unchanged}
          >
            {loading ? t('groups.moveModal.moving') : t('groups.moveModal.move')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
